if (!customElements.get('comparison-story')) {
  customElements.define(
    'comparison-story',
    class ComparisonStory extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-comparison-story-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-comparison-story-panel]'));
        this.scroller = this.querySelector('[data-comparison-story-scroll]');
        this.previousButton = this.querySelector('[data-comparison-story-prev]');
        this.nextButton = this.querySelector('[data-comparison-story-next]');
        this.currentLabel = this.querySelector('[data-comparison-story-current]');
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.autoAdvanceDelay = 6000;
        this.autoAdvanceTimer = null;
        this.activeIndex = 0;

        if (!this.tabs.length || !this.panels.length) return;

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.activateFromUser(index));
          tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
        });

        this.previousButton?.addEventListener('click', () => {
          this.activateFromUser((this.activeIndex - 1 + this.tabs.length) % this.tabs.length);
        });

        this.nextButton?.addEventListener('click', () => {
          this.activateFromUser((this.activeIndex + 1) % this.tabs.length);
        });

        this.addEventListener('pointerenter', () => this.pauseAutoAdvance());
        this.addEventListener('pointerleave', () => this.startAutoAdvance());
        this.addEventListener('focusin', () => this.pauseAutoAdvance());
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.startAutoAdvance();
        });

        this.setActive(0, false);
        this.startAutoAdvance();
      }

      disconnectedCallback() {
        this.pauseAutoAdvance(false);
      }

      setActive(activeIndex, moveFocus = false) {
        this.activeIndex = activeIndex;
        this.dataset.activeStage = String(activeIndex + 1);
        this.style.setProperty('--active-stage', activeIndex);
        if (this.currentLabel) this.currentLabel.textContent = String(activeIndex + 1).padStart(2, '0');

        this.tabs.forEach((tab, index) => {
          const isActive = index === activeIndex;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.setAttribute('tabindex', isActive ? '0' : '-1');

          if (isActive && moveFocus) {
            tab.focus();

            if (this.scroller && this.scroller.scrollWidth > this.scroller.clientWidth) {
              const tabRect = tab.getBoundingClientRect();
              const scrollerRect = this.scroller.getBoundingClientRect();
              const centeredOffset = tabRect.left - scrollerRect.left - (scrollerRect.width - tabRect.width) / 2;

              this.scroller.scrollBy({
                left: centeredOffset,
                behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
              });
            }
          }
        });

        this.panels.forEach((panel, index) => {
          const isActive = index === activeIndex;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });
      }

      activateFromUser(activeIndex, moveFocus = false) {
        this.setActive(activeIndex, moveFocus);
        this.startAutoAdvance(true);
      }

      pauseAutoAdvance(showPausedState = true) {
        window.clearTimeout(this.autoAdvanceTimer);
        this.autoAdvanceTimer = null;

        if (showPausedState) this.setAttribute('data-paused', '');
      }

      startAutoAdvance(resetProgress = false) {
        this.pauseAutoAdvance(false);

        if (
          this.prefersReducedMotion ||
          this.matches(':hover') ||
          this.contains(document.activeElement)
        ) {
          this.setAttribute('data-paused', '');
          return;
        }

        this.removeAttribute('data-paused');
        if (resetProgress) this.restartProgress();

        this.autoAdvanceTimer = window.setTimeout(() => {
          this.setActive((this.activeIndex + 1) % this.tabs.length);
          this.startAutoAdvance(true);
        }, this.autoAdvanceDelay);
      }

      restartProgress() {
        this.setAttribute('data-reset-progress', '');
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => this.removeAttribute('data-reset-progress'));
        });
      }

      handleKeydown(event, currentIndex) {
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (currentIndex + 1) % this.tabs.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = this.tabs.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        this.activateFromUser(nextIndex, true);
      }
    }
  );
}
